import {
  Alert,
  App,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, type ApiError } from "../services/api";
import { useAuth } from "../services/auth";

interface ChannelServiceControlStatus {
  service: "qq_official" | "live_runtime";
  environment: "development" | "production";
  enabled: boolean;
  active: boolean;
  canEnable: boolean;
  requirements: Record<string, boolean>;
  updatedAt: string | null;
  enableConfirmation: string;
  disableConfirmation: string;
}

interface ChannelServiceControlCardProps {
  queryKey: string;
  endpoint: string;
  title: string;
  enabledMessage: string;
  disabledMessage: string;
  description: string;
  requirementLabels: Record<string, string>;
  onChanged?: () => void | Promise<unknown>;
}

export function ChannelServiceControlCard({
  queryKey,
  endpoint,
  title,
  enabledMessage,
  disabledMessage,
  description,
  requirementLabels,
  onChanged,
}: ChannelServiceControlCardProps) {
  const { can } = useAuth();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<{ confirmation: string; reason: string }>();
  const [pendingEnabled, setPendingEnabled] = useState<boolean | null>(null);
  const query = useQuery({
    queryKey: [queryKey],
    queryFn: () => api.get<ChannelServiceControlStatus>(endpoint),
  });
  const mutation = useMutation({
    mutationFn: (input: {
      enabled: boolean;
      confirmation: string;
      reason: string;
      expectedUpdatedAt: string | null;
    }) => api.patch<ChannelServiceControlStatus>(endpoint, input),
    onSuccess: async (next) => {
      queryClient.setQueryData([queryKey], next);
      setPendingEnabled(null);
      form.resetFields();
      await onChanged?.();
      message.success(next.enabled ? `${title}已开启` : `${title}已关闭`);
    },
    onError: (error: Error) => {
      const apiError = error as ApiError;
      message.error(
        apiError.requestId
          ? `${apiError.message}（${apiError.code}，请求 ${apiError.requestId}）`
          : apiError.message,
        8,
      );
    },
  });

  if (query.error) {
    return <Alert type="error" showIcon message={(query.error as Error).message} />;
  }

  const status = query.data;
  const canToggle = can("channel.service.toggle");
  const expectedConfirmation = pendingEnabled
    ? status?.enableConfirmation
    : status?.disableConfirmation;
  const openConfirmation = (enabled: boolean) => {
    form.resetFields();
    setPendingEnabled(enabled);
  };
  const closeConfirmation = () => {
    if (!mutation.isPending) setPendingEnabled(null);
  };
  const submit = async () => {
    if (pendingEnabled === null || !status) return;
    const values = await form.validateFields();
    mutation.mutate({
      enabled: pendingEnabled,
      confirmation: values.confirmation.trim(),
      reason: values.reason.trim(),
      expectedUpdatedAt: status.updatedAt,
    });
  };

  return (
    <>
      <Card
        title={title}
        loading={query.isLoading}
        extra={status ? (
          <Space>
            {!canToggle ? <Tag>只读</Tag> : null}
            <Switch
              checked={status.enabled}
              checkedChildren="已开启"
              unCheckedChildren="已关闭"
              disabled={
                !canToggle ||
                mutation.isPending ||
                (!status.enabled && !status.canEnable)
              }
              onChange={openConfirmation}
            />
          </Space>
        ) : null}
      >
        {status ? (
          <Space direction="vertical" size={10} style={{ width: "100%" }}>
            <Alert
              type={status.active ? "success" : status.enabled ? "error" : "info"}
              showIcon
              message={status.active ? enabledMessage : status.enabled ? "开关已开启，但启动条件不完整" : disabledMessage}
              description={description}
            />
            <Space wrap>
              <Tag color={status.environment === "production" ? "purple" : "blue"}>
                {status.environment === "production" ? "线上环境" : "测试环境"}
              </Tag>
              {Object.entries(status.requirements).map(([key, ready]) => (
                <Tag key={key} color={ready ? "success" : "error"}>
                  {requirementLabels[key] ?? key}：{ready ? "就绪" : "未就绪"}
                </Tag>
              ))}
            </Space>
            <Typography.Text type="secondary">
              本开关只控制当前 {status.environment} 环境；最近变更：
              {status.updatedAt ? new Date(status.updatedAt).toLocaleString("zh-CN") : "尚未设置"}
            </Typography.Text>
          </Space>
        ) : null}
      </Card>

      <Modal
        title={`${pendingEnabled ? "开启" : "关闭"}${title}`}
        open={pendingEnabled !== null}
        okText={pendingEnabled ? "确认开启" : "确认关闭"}
        okButtonProps={{ danger: pendingEnabled === false }}
        confirmLoading={mutation.isPending}
        onCancel={closeConfirmation}
        onOk={() => void submit()}
      >
        <Alert
          type={pendingEnabled ? "warning" : "error"}
          showIcon
          style={{ marginBottom: 16 }}
          message={
            pendingEnabled
              ? "开启后当前环境会开始响应外部平台活动。请先确认另一环境的同类服务已经关闭。"
              : "关闭会立即停止当前环境响应外部平台活动。"
          }
        />
        <Form form={form} layout="vertical">
          <Form.Item
            name="confirmation"
            label={`输入“${expectedConfirmation ?? "确认短语"}”`}
            rules={[
              { required: true, whitespace: true, message: "请输入确认短语" },
              {
                validator: (_, value: string | undefined) =>
                  value?.trim() === expectedConfirmation
                    ? Promise.resolve()
                    : Promise.reject(new Error(`请输入“${expectedConfirmation ?? "确认短语"}”`)),
              },
            ]}
          >
            <Input autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="reason"
            label="操作原因"
            rules={[
              { required: true, whitespace: true, message: "请填写操作原因" },
              { min: 4, message: "请填写至少 4 个字" },
              { max: 200 },
            ]}
          >
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
